import { z } from 'zod';

/**
 * Every HTTP response is parsed through one of these.
 *
 * The API client returns validated types, so nothing downstream needs a defensive check and
 * nothing is cast with `as`. A response cast rather than parsed is an unchecked assumption that
 * surfaces later as a runtime error in a component that had no way to know.
 *
 * Enums are STRING unions matching the API's `JsonStringEnumConverter`. As numbers, the client
 * would need its own copy of the ordering, which silently means something different the first
 * time a member is inserted rather than appended.
 */

export const CampaignStatusSchema = z.enum([
  'Draft',
  'InReview',
  'Approved',
  'Scheduled',
  'Sending',
  'Sent',
  'Archived',
  // Added when pause/cancel was built. Distinct from Archived, which is a filing decision, and from
  // paused, which can still resume.
  'Cancelled',
]);

export type CampaignStatus = z.infer<typeof CampaignStatusSchema>;

export const CampaignModeSchema = z.enum(['Batched', 'PerRecipient']);

export const LintSeveritySchema = z.enum(['Info', 'Warning', 'Fatal']);
export type LintSeverity = z.infer<typeof LintSeveritySchema>;

export const LintCategorySchema = z.enum([
  'Compliance',
  'Deliverability',
  'Outlook',
  'Structure',
  'ClickTracking',
  'Accessibility',
  'DarkMode',
  'Info',
  'Liquid',
]);

export type LintCategory = z.infer<typeof LintCategorySchema>;

export const LintFindingSchema = z.object({
  ruleId: z.string(),
  severity: LintSeveritySchema,
  category: LintCategorySchema,
  message: z.string(),
  // Nullable because some rules are about the document as a whole — total size, a missing
  // unsubscribe link — and inventing a line number for those would send the author somewhere
  // arbitrary.
  line: z.number().int().nullable(),
  suggestion: z.string().nullable(),
  excerpt: z.string().nullable(),
});

export type LintFinding = z.infer<typeof LintFindingSchema>;

export const LintResponseSchema = z.object({
  findings: z.array(LintFindingSchema),
  fatalCount: z.number().int(),
  warningCount: z.number().int(),
  infoCount: z.number().int(),
  blocksSend: z.boolean(),
  byteSize: z.number().int(),
  variantCount: z.number().int(),
  estimatedRequestCount: z.number().int(),
  placeholderKeys: z.array(z.string()),
  requiresPerRecipientMode: z.boolean(),
  previewHtml: z.string(),
  plainText: z.string(),
});

export type LintResponse = z.infer<typeof LintResponseSchema>;

export const ArmSchema = z.object({
  id: z.string().uuid(),
  label: z.string(),
  subject: z.string(),
  preheader: z.string().nullable(),
  fromName: z.string().nullable(),
  htmlAuthored: z.string(),
  plainText: z.string().nullable(),
  isPlainTextHandEdited: z.boolean(),
  splitPercent: z.number(),
  isHoldback: z.boolean(),
  isWinner: z.boolean(),
  updatedAt: z.string(),
});

export type Arm = z.infer<typeof ArmSchema>;

export const CampaignSummarySchema = z.object({
  id: z.string().uuid(),
  brandId: z.string().uuid(),
  brandName: z.string(),
  name: z.string(),
  status: CampaignStatusSchema,
  mode: CampaignModeSchema,
  armCount: z.number().int(),
  scheduledAt: z.string().nullable(),
  sentAt: z.string().nullable(),
  audienceSize: z.number().int().nullable(),
  variantCount: z.number().int().nullable(),
  tags: z.array(z.string()),
  updatedAt: z.string(),
});

export type CampaignSummary = z.infer<typeof CampaignSummarySchema>;

export const CampaignDetailSchema = z.object({
  id: z.string().uuid(),
  brandId: z.string().uuid(),
  brandName: z.string(),
  name: z.string(),
  status: CampaignStatusSchema,
  mode: CampaignModeSchema,
  senderIdentityId: z.string().uuid().nullable(),
  segmentId: z.string().uuid().nullable(),
  excludeSegmentId: z.string().uuid().nullable(),
  topicId: z.string().uuid().nullable(),
  openTrackingEnabled: z.boolean(),
  clickTrackingEnabled: z.boolean(),
  buildStepEnabled: z.boolean(),
  utmCampaign: z.string().nullable(),
  scheduledAt: z.string().nullable(),
  sentAt: z.string().nullable(),
  batchId: z.string().nullable(),
  isPaused: z.boolean(),
  pauseReason: z.string().nullable(),
  breakerTrippedAt: z.string().nullable(),
  breakerTripReason: z.string().nullable(),
  audienceSize: z.number().int().nullable(),
  suppressedCount: z.number().int().nullable(),
  variantCount: z.number().int().nullable(),
  estimatedRequestCount: z.number().int().nullable(),
  archiveSlug: z.string().nullable(),
  notes: z.string().nullable(),
  tags: z.array(z.string()),
  arms: z.array(ArmSchema),
  createdBy: z.string(),
  createdAt: z.string(),
  updatedAt: z.string(),
});

export type CampaignDetail = z.infer<typeof CampaignDetailSchema>;

export const VersionSummarySchema = z.object({
  id: z.string().uuid(),
  versionNumber: z.number().int(),
  subject: z.string(),
  message: z.string().nullable(),
  createdBy: z.string(),
  createdAt: z.string(),
  byteSize: z.number().int(),
});

export type VersionSummary = z.infer<typeof VersionSummarySchema>;

export const VersionDetailSchema = z.object({
  id: z.string().uuid(),
  versionNumber: z.number().int(),
  html: z.string(),
  subject: z.string(),
  preheader: z.string().nullable(),
  message: z.string().nullable(),
  createdBy: z.string(),
  createdAt: z.string(),
});

export type VersionDetail = z.infer<typeof VersionDetailSchema>;

export function pagedSchema<T extends z.ZodTypeAny>(item: T) {
  return z.object({
    items: z.array(item),
    total: z.number().int(),
    skip: z.number().int(),
    take: z.number().int(),
  });
}

/**
 * The single error shape every failure returns — including auth failures, which the API routes
 * through the same shape rather than the framework's empty 401 body.
 */
export const ProblemSchema = z.object({
  error: z.string(),
  message: z.string(),
  correlationId: z.string(),
  errors: z.record(z.string(), z.array(z.string())).nullable().optional(),
});

export type Problem = z.infer<typeof ProblemSchema>;

// ── Contacts ─────────────────────────────────────────────────────────────────

export const ContactStatusSchema = z.enum(['Active', 'PendingConfirmation', 'Erased']);
export type ContactStatus = z.infer<typeof ContactStatusSchema>;

export const ContactSummarySchema = z.object({
  id: z.string().uuid(),
  email: z.string(),
  firstName: z.string().nullable(),
  lastName: z.string().nullable(),
  status: ContactStatusSchema,
  consentAt: z.string().nullable(),
  lastOpenedAt: z.string().nullable(),
  createdAt: z.string(),
});

export type ContactSummary = z.infer<typeof ContactSummarySchema>;

export const ContactImportErrorSchema = z.object({
  rowNumber: z.number().int(),
  reason: z.string(),
});

export const ContactImportResponseSchema = z.object({
  totalRows: z.number().int(),
  created: z.number().int(),
  updated: z.number().int(),
  skipped: z.number().int(),
  invalid: z.number().int(),
  errors: z.array(ContactImportErrorSchema),
});

export type ContactImportResult = z.infer<typeof ContactImportResponseSchema>;

// ── Segments ─────────────────────────────────────────────────────────────────

// Mirrors the closed enumerations in SegmentRule.cs. Closed on purpose: the compiler only accepts
// what it can translate, and a free-text field here would be a field the server has to reject.
export const SegmentFieldSchema = z.enum([
  'Email',
  'FirstName',
  'LastName',
  'Status',
  'CustomField',
  'ConsentSource',
  'ConsentAt',
  'ConfirmedAt',
  'CreatedAt',
  'LastDeliveredAt',
  'LastOpenedAt',
  'LastClickedAt',
  'ConsecutiveSoftBounces',
  'OpenedCampaign',
  'ClickedCampaign',
  'ReceivedCampaign',
  'ListMembership',
  'TopicSubscription',
]);

export type SegmentField = z.infer<typeof SegmentFieldSchema>;

export const SegmentOperatorSchema = z.enum([
  'Equals',
  'NotEquals',
  'Contains',
  'StartsWith',
  'IsSet',
  'IsNotSet',
  'GreaterThan',
  'LessThan',
  'WithinLastDays',
  'NotWithinLastDays',
  'IsTrue',
  'IsFalse',
]);

export type SegmentOperator = z.infer<typeof SegmentOperatorSchema>;

export const SegmentConditionSchema = z.object({
  field: SegmentFieldSchema,
  operator: SegmentOperatorSchema,
  value: z.string().nullable(),
  customFieldKey: z.string().nullable(),
  campaignId: z.string().uuid().nullable(),
});

export type SegmentCondition = z.infer<typeof SegmentConditionSchema>;

// Recursive, so a nested group needs z.lazy. Depth is capped server-side at 5; the builder does not
// currently offer nesting, but the schema has to round-trip a rule that has it.
export type SegmentGroup = {
  combinator: 'And' | 'Or';
  conditions: SegmentCondition[];
  groups: SegmentGroup[];
};

export const SegmentGroupSchema: z.ZodType<SegmentGroup> = z.lazy(() =>
  z.object({
    combinator: z.enum(['And', 'Or']),
    conditions: z.array(SegmentConditionSchema),
    groups: z.array(SegmentGroupSchema),
  }),
);

/**
 * One link in a campaign, for the per-link table (spec § 4.7).
 *
 * The same shape the report's `topLinks` carries, exposed separately because the dedicated endpoint
 * returns EVERY link in document order rather than the top 50 by clicks — which is the ordering the
 * spec said carries the value, and the one that keeps the zero-click links.
 */
export const CampaignLinkSchema = z.object({
  url: z.string(),
  linkText: z.string().nullable(),
  position: z.number().int(),
  totalClicks: z.number().int(),
  uniqueClicks: z.number().int(),
  humanTotalClicks: z.number().int(),
});

export type CampaignLink = z.infer<typeof CampaignLinkSchema>;

export const SegmentSummarySchema = z.object({
  id: z.string().uuid(),
  brandId: z.string().uuid(),
  name: z.string(),
  description: z.string().nullable(),
  lastCount: z.number().int().nullable(),
  lastCountedAt: z.string().nullable(),
  updatedAt: z.string(),
});

export type SegmentSummary = z.infer<typeof SegmentSummarySchema>;

export const SegmentDetailSchema = z.object({
  id: z.string().uuid(),
  brandId: z.string().uuid(),
  name: z.string(),
  description: z.string().nullable(),
  rule: SegmentGroupSchema,
  sql: z.string().nullable(),
  lastCount: z.number().int().nullable(),
  lastCountedAt: z.string().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
});

export type SegmentDetail = z.infer<typeof SegmentDetailSchema>;

export const SegmentPreviewSchema = z.object({
  total: z.number().int(),
  sampleAddresses: z.array(z.string()),
  sql: z.string(),
});

export type SegmentPreview = z.infer<typeof SegmentPreviewSchema>;

// ── Pre-send checklist ───────────────────────────────────────────────────────

export const ChecklistSeveritySchema = z.enum(['Information', 'Warning', 'Blocking']);
export type ChecklistSeverity = z.infer<typeof ChecklistSeveritySchema>;

export const ChecklistItemSchema = z.object({
  key: z.string(),
  severity: ChecklistSeveritySchema,
  summary: z.string(),
  detail: z.string().nullable(),
});

export type ChecklistItem = z.infer<typeof ChecklistItemSchema>;

export const PreSendChecklistSchema = z.object({
  campaignId: z.string().uuid(),
  audienceSize: z.number().int(),
  canSend: z.boolean(),
  blockingCount: z.number().int(),
  warningCount: z.number().int(),
  items: z.array(ChecklistItemSchema),
});

export type PreSendChecklist = z.infer<typeof PreSendChecklistSchema>;

// ── Campaign report ──────────────────────────────────────────────────────────

export const RateSetSchema = z.object({
  sent: z.number().int(),
  delivered: z.number().int(),
  hardBounced: z.number().int(),
  blocked: z.number().int(),
  dropped: z.number().int(),
  uniqueOpens: z.number().int(),
  uniqueHumanOpens: z.number().int(),
  totalOpens: z.number().int(),
  totalHumanOpens: z.number().int(),
  uniqueClicks: z.number().int(),
  uniqueHumanClicks: z.number().int(),
  totalClicks: z.number().int(),
  totalHumanClicks: z.number().int(),
  unsubscribed: z.number().int(),
  complained: z.number().int(),
  bounceRate: z.number(),
  deliveryRate: z.number(),
  openRate: z.number(),
  humanOpenRate: z.number(),
  clickRate: z.number(),
  humanClickRate: z.number(),
  clickToOpenRate: z.number(),
  unsubscribeRate: z.number(),
  complaintRate: z.number(),
});

export type RateSet = z.infer<typeof RateSetSchema>;

export const CampaignReportSchema = z.object({
  campaignId: z.string().uuid(),
  name: z.string(),
  status: CampaignStatusSchema,
  sendingStartedAt: z.string().nullable(),
  sentAt: z.string().nullable(),
  overall: RateSetSchema,
  arms: z.array(
    z.object({
      armId: z.string().uuid(),
      label: z.string(),
      subject: z.string(),
      isWinner: z.boolean(),
      rates: RateSetSchema,
    }),
  ),
  topLinks: z.array(
    z.object({
      url: z.string(),
      linkText: z.string().nullable(),
      position: z.number().int(),
      totalClicks: z.number().int(),
      uniqueClicks: z.number().int(),
      humanTotalClicks: z.number().int(),
    }),
  ),
  timeline: z.array(
    z.object({
      day: z.string(),
      delivered: z.number().int(),
      opens: z.number().int(),
      clicks: z.number().int(),
      bounces: z.number().int(),
      complaints: z.number().int(),
      unsubscribes: z.number().int(),
    }),
  ),
});

export type CampaignReport = z.infer<typeof CampaignReportSchema>;

// ── User guide ───────────────────────────────────────────────────────────────

export const HelpAudienceSchema = z.enum(['staff', 'admin']);
export type HelpAudience = z.infer<typeof HelpAudienceSchema>;

/**
 * `bodyHtml` arrives already sanitised — server-side, with an allowlist, on the way into storage.
 * The client neither sanitises it again nor is trusted to: a client-side sanitiser protects nobody
 * who is not running our client.
 */
export const HelpArticleSchema = z.object({
  id: z.string().uuid(),
  slug: z.string(),
  audience: HelpAudienceSchema,
  section: z.string(),
  sortOrder: z.number().int(),
  title: z.string(),
  bodyHtml: z.string(),
  updatedAt: z.string(),
  updatedBy: z.string().nullable(),
});

export type HelpArticle = z.infer<typeof HelpArticleSchema>;

// ── Per-recipient timeline ───────────────────────────────────────────────────

export const EmailEventTypeSchema = z.enum([
  'Processed',
  'Dropped',
  'Delivered',
  'Deferred',
  'Bounce',
  'Open',
  'Click',
  'SpamReport',
  'Unsubscribe',
  'GroupUnsubscribe',
  'GroupResubscribe',
  'AccountStatusChange',
]);

export type EmailEventType = z.infer<typeof EmailEventTypeSchema>;

export const RecipientSendSummarySchema = z.object({
  campaignId: z.string().uuid(),
  campaignName: z.string(),
  armId: z.string().uuid(),
  createdAt: z.string(),
  deliveredAt: z.string().nullable(),
  droppedAt: z.string().nullable(),
  droppedReason: z.string().nullable(),
  bouncedAt: z.string().nullable(),
  bounceType: z.string().nullable(),
  bounceReason: z.string().nullable(),
  firstOpenedAt: z.string().nullable(),
  humanOpenCount: z.number().int(),
  openCount: z.number().int(),
  firstClickedAt: z.string().nullable(),
  humanClickCount: z.number().int(),
  clickCount: z.number().int(),
  unsubscribedAt: z.string().nullable(),
  complainedAt: z.string().nullable(),
});

export type RecipientSendSummary = z.infer<typeof RecipientSendSummarySchema>;

export const RecipientEventSchema = z.object({
  campaignId: z.string().uuid().nullable(),
  type: EmailEventTypeSchema,
  occurredAt: z.string(),
  url: z.string().nullable(),
  reason: z.string().nullable(),
  bounceType: z.string().nullable(),
  isMachineOpen: z.boolean(),
  isSuspectedBot: z.boolean(),
  botReason: z.string().nullable(),
  emailClient: z.string().nullable(),
  deviceType: z.string().nullable(),
  operatingSystem: z.string().nullable(),
  countryCode: z.string().nullable(),
});

export type RecipientEvent = z.infer<typeof RecipientEventSchema>;

export const RecipientTimelineSchema = z.object({
  contactId: z.string().uuid(),
  email: z.string(),
  sends: z.array(RecipientSendSummarySchema),
  events: z.array(RecipientEventSchema),
  nextBefore: z.string().nullable(),
});

export type RecipientTimeline = z.infer<typeof RecipientTimelineSchema>;

// ── Brands and sender identities ─────────────────────────────────────────────

export const SenderIdentitySummarySchema = z.object({
  id: z.string().uuid(),
  label: z.string(),
  fromLocalPart: z.string(),
  fromName: z.string(),
  replyToAddress: z.string().nullable(),
  /** Computed from the brand's domain server-side, never stored — so it cannot go stale. */
  fromAddress: z.string(),
  hasDmarcWarning: z.boolean(),
  isActive: z.boolean(),
});

export type SenderIdentitySummary = z.infer<typeof SenderIdentitySummarySchema>;

export const BreakerThresholdsSchema = z.object({
  hardBouncePercent: z.number(),
  complaintPercent: z.number(),
  blockPercent: z.number(),
  minimumDelivered: z.number().int(),
});

export type BreakerThresholds = z.infer<typeof BreakerThresholdsSchema>;

export const BrandSummarySchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  slug: z.string(),
  fromDomain: z.string(),
  requiresApproval: z.boolean(),
  isActive: z.boolean(),
  senderIdentityCount: z.number().int(),
  updatedAt: z.string(),
});

export type BrandSummary = z.infer<typeof BrandSummarySchema>;

export const FrequencyCapSchema = z.object({
  maxSends: z.number().int(),
  windowDays: z.number().int(),
});

export type FrequencyCap = z.infer<typeof FrequencyCapSchema>;

export const TopicSummarySchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  description: z.string(),
  isActive: z.boolean(),
});

export type TopicSummary = z.infer<typeof TopicSummarySchema>;

export const BrandDetailSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  slug: z.string(),
  fromDomain: z.string(),
  systemFromDomain: z.string(),
  defaultFromName: z.string(),
  replyToAddress: z.string().nullable(),
  /** Read-only. It reflects DNS SendGrid verified, so no form may set it. */
  ipPoolName: z.string().nullable(),
  footerHtml: z.string(),
  postalAddress: z.string(),
  segmentationAttributes: z.array(z.string()),
  breakerThresholds: BreakerThresholdsSchema,
  /**
   * Null means no cap, which is the default. Null rather than zeroes for "off": a payload carrying
   * `{maxSends: 0}` makes every reader re-derive whether that means "no cap" or "cap everything", and
   * one of those answers empties an audience.
   */
  frequencyCap: FrequencyCapSchema.nullable(),
  requiresApproval: z.boolean(),
  isActive: z.boolean(),
  senderIdentities: z.array(SenderIdentitySummarySchema),
  /** Includes retired topics, with the flag — a campaign that declared one before it was retired still
      needs its name resolvable. Pickers filter on isActive. */
  topics: z.array(TopicSummarySchema),
  createdAt: z.string(),
  updatedAt: z.string(),
});

export type BrandDetail = z.infer<typeof BrandDetailSchema>;
